import React, { useState, useEffect } from 'react';
import { useInfiniteHits } from 'react-instantsearch';
import { Button, DOCUMENT_MEDIA_PHOTO } from '@bwoty-web/styleguide';

import { HotelHitAlgolia } from './HotelHitAlgolia';
import ImageBlockGeo from './ImageBlockGeo';
import FaqHit from './FaqHit';

import './Hits.scss';

function NoResultInformation({ text = '', searching = false, allSearchResults }) {
  if (searching || !allSearchResults) {
    return null;
  }
  return <div className="web-result-no-result">{text}</div>;
}

function LoadMoreButton({ hide, refine, label, searching = false }) {
  if (hide) {
    return null;
  }
  return (
    <div className="web-result-hits__show-more-button">
      <Button
        onClick={() => {
          refine();
        }}
        rel="nofollow"
        target="_blank"
        loading={searching}
        disabled={searching}
      >
        {label}
      </Button>
    </div>
  );
}

function Hit({ hit, texts, expanded, toggleExpand, imageDomain }) {
  switch (hit.type) {
    case 'hotel':
      return <HotelHitAlgolia content={hit} texts={texts} imageDomain={imageDomain} />;
    case 'faq':
      return (
        <FaqHit
          hit={hit}
          texts={texts}
          toggleExpand={toggleExpand}
          expanded={expanded === hit.objectID}
        />
      );
    case 'umbraco':
      return (
        <ImageBlockGeo
          algolia={hit}
          texts={texts}
          icon={{
            path: DOCUMENT_MEDIA_PHOTO,
            sizeMobile: 'm',
          }}
          imageDomain={imageDomain}
        />
      );
    case 'region':
    case 'area':
    case 'resort':
    case 'country':
      return (
        <ImageBlockGeo
          algolia={hit}
          texts={texts}
          icon={{
            path: DOCUMENT_MEDIA_PHOTO,
            sizeMobile: 'm',
          }}
          imageDomain={imageDomain}
        />
      );
    default:
      return <div>{hit.name}</div>;
  }
}

function Hits(props) {
  const { items, showMore, isLastPage, results } = useInfiniteHits();
  const [expanded, setExpanded] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [allItems, setAllItems] = useState([]);

  useEffect(() => {
    if (items && items.length > 0) {
      setAllItems((prevItems) => {
        const existingSet = new Set(prevItems.map((item) => item.objectID));
        const newItems = items.filter((item) => !existingSet.has(item.objectID));
        return [...prevItems, ...newItems];
      });
    }
  }, [items]);

  const { texts, imageDomain } = props;

  const handleShowMore = () => {
    setIsLoading(true);
    showMore();
    setTimeout(() => setIsLoading(false), 500);
  };

  const toggleExpand = (id) => {
    if (id) {
      setExpanded(expanded === id ? null : id);
    }
  };

  if (results && results.nbHits === 0) {
    return <NoResultInformation text={texts.noHitsInformation} allSearchResults={results} />;
  }

  return (
    <div>
      <div className="web-result-hits">
        {allItems?.map((hit) => (
          <Hit
            key={hit.objectID}
            hit={hit}
            texts={texts}
            expanded={expanded}
            toggleExpand={toggleExpand}
            imageDomain={imageDomain}
          />
        ))}
      </div>
      <LoadMoreButton
        hide={isLastPage}
        refine={handleShowMore}
        label={texts.getMore}
        searching={isLoading}
      />
    </div>
  );
}

export default Hits;
